import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Botao from '@/components/ui/Botao';
import { DICAS } from '@/conteudo/dicas';

interface DicaPrimeiraVezProps {
  id: string;
  eventoId?: number;
  className?: string;
  /** Sobrescreve o título da dica (para textos que dependem do evento). */
  titulo?: string;
  /** Sobrescreve o texto da dica. */
  texto?: string;
}

// Cartão de ajuda que aparece só na primeira vez (guarda em localStorage).
// Se o navegador bloquear o storage, aparece sempre.
export default function DicaPrimeiraVez({
  id,
  eventoId,
  className = '',
  titulo,
  texto,
}: DicaPrimeiraVezProps) {
  const navigate = useNavigate();
  const params = useParams();
  const dica = DICAS[id];
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(`dica-vista:${id}`) === '1') {
        setVisivel(false);
        return;
      }
    } catch {
      /* sem storage: mostra sempre */
    }
    setVisivel(true);
  }, [id]);

  if (!dica || !visivel) return null;

  const idEvento = eventoId ?? numeroDoParametro(params.eventoId) ?? numeroDoParametro(params.id);

  function fechar() {
    try {
      localStorage.setItem(`dica-vista:${id}`, '1');
    } catch {
      /* sem storage: nada a guardar */
    }
    setVisivel(false);
  }

  function irProximo() {
    if (!dica?.proximo) return;
    const { para } = dica.proximo;
    if (para.startsWith('#')) {
      window.dispatchEvent(new CustomEvent('seliga:dica', { detail: para.slice(1) }));
      return;
    }
    navigate(para.includes(':id') && idEvento ? para.replace(':id', String(idEvento)) : para);
  }

  return (
    <div className={`relative rounded-3xl border border-primary-100 bg-primary-50 p-4 ${className}`}>
      <button
        type="button"
        onClick={fechar}
        aria-label="Fechar dica"
        className="absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-primary-500 transition-colors hover:bg-primary-100"
      >
        <i className="ri-close-line text-lg" />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
          <i className="ri-lightbulb-line text-lg" />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-foreground-950">{titulo ?? dica.titulo}</p>
          <p className="mt-1 text-[14px] leading-relaxed text-foreground-700">
            {texto ?? dica.texto}
          </p>
          {dica.proximo && (
            <div className="mt-3">
              <Botao icone="ri-arrow-right-line" onClick={irProximo}>
                {dica.proximo.rotulo}
              </Botao>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function numeroDoParametro(valor?: string): number | undefined {
  if (!valor) return undefined;
  const n = Number(valor);
  return Number.isFinite(n) ? n : undefined;
}